/**
 * Modal component script class
 *
 * @package    Common
 * @author     Vadym Karpenko <vadym.karpenko@door3.com>
 */

import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import cx from 'classnames';

import './styles.scss';

export default class Modal extends PureComponent {
    /**
     * @type {Object}
     */
    static propTypes = {
        children: PropTypes.oneOfType([
            PropTypes.arrayOf(PropTypes.node),
            PropTypes.node,
        ]),
        close: PropTypes.func,
        description: PropTypes.string,
        show: PropTypes.bool.isRequired,
        size: PropTypes.oneOf([
            'sm',
            'md',
            'lg',
            'xl',
        ]),
        submit: PropTypes.func,
        submitDisabled: PropTypes.bool,
        submitLabel: PropTypes.string,
        title: PropTypes.string.isRequired,
    };

    /**
     * @type {Object}
     */
    static defaultProps = {
        children: undefined,
        close: undefined,
        description: '',
        size: 'md',
        submit: undefined,
        submitDisabled: false,
        submitLabel: '',
    };

    /**
     * @type {Object}
     */
    state = {
        mounted: false,
        visible: false,
    };

    componentDidUpdate (prevProps) {
        const { show } = this.props;

        if (!prevProps.show && show) {
            document.body.classList.add('modal-open');
            document.body.classList.add('o-hidden');
            this.setState({
                mounted: true,
            });
            this.timeout = setTimeout(() => {
                this.setState({
                    visible: true,
                });
            }, 300);
        }
        if (prevProps.show && !show) {
            document.body.classList.remove('modal-open');
            document.body.classList.remove('o-hidden');
            this.setState({
                visible: false,
            });
            this.timeout = setTimeout(() => {
                this.setState({
                    mounted: false,
                });
            }, 300);
        }
    }

    timeout = null;

    render () {
        const {
            children,
            close,
            description,
            size,
            submit,
            submitDisabled,
            submitLabel,
            title,
        } = this.props;
        const {
            mounted,
            visible,
        } = this.state;

        if (!mounted) {
            return null;
        }

        return ReactDOM.createPortal(
            <>
                <div
                    className={ cx('modal-backdrop', 'fade', {
                        show: visible,
                    }) }
                />
                <div
                    aria-hidden="true"
                    aria-modal="true"
                    className={ cx('modal', 'fade', { show: visible }) }
                    role="dialog"
                    tabIndex="-1"
                >
                    <div className={ cx('modal-dialog', `modal-${ size }`) } role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <div className="modal-title h4">{ title }</div>
                                { close && <button type="button" className="close" onClick={ close }>
                                    <span aria-hidden="true">×</span>
                                    <span className="sr-only">
                                        <FormattedMessage id="buttons.close"/>
                                    </span>
                                </button> }
                            </div>

                            <div className="modal-body">
                                { children || description }
                            </div>

                            { (close || submit) && <div className="modal-footer">
                                { close && <button type="button" className="btn btn-outline-primary" onClick={ close }>
                                    <FormattedMessage id="buttons.close"/>
                                </button> }
                                { submit && <button
                                    type="button" className="btn btn-primary" onClick={ submit }
                                    disabled={ submitDisabled }
                                >
                                    <FormattedMessage id={ `buttons.${ submitLabel }` }/>
                                </button> }
                            </div> }
                        </div>
                    </div>
                </div>
            </>,
            document.body,
        );
    }
}
